/*
 * Spore segmentation from a painted mask: pure functions on typed arrays, no
 * DOM, no canvas, so the same code runs in the browser and under Vitest on a
 * synthetic image (detect.test.js draws ellipses of known size).
 *
 * Spore analysis (components/workbench/microscopy/MicroscopyMode.js) works on a
 * MASK of the spores, on the analysis grid -- the picture drawn at most
 * DEFAULTS.gridMaxSide (2048) px on its long side (raster.analysisSize), so a
 * cellSens micrograph (1920 x 1200) is read pixel for pixel:
 *
 *   threshold   grayscale() is the picture's grey level, 0-255 (Rec. 601
 *               luma) after a Gaussian blur of sigma 1 px, so grain does not
 *               split a spore. The preview is filters.thresholdPreview (the
 *               brightness band, the colour and the particle size);
 *               autoThreshold() puts the band at Otsu's threshold, on the
 *               side the picture's brightness says the spores are: a picture
 *               lit from behind (brightfield, a mean grey level of at least
 *               `brightfieldMean`) has dark spores, a darker one (darkfield,
 *               fluorescence) light ones -- never "the side with fewer
 *               pixels", which a dense spore deposit turns upside down.
 *               applyThreshold() adds the preview to the mask with its
 *               enclosed holes up to 2% of the picture filled -- a spore seen
 *               as a dark wall around a bright centre is one object.
 *   colourless  autoDiffers() is "Auto (colourless spores)": a hyaline spore
 *               at 100x is a dark rim round a brighter centre on a field of
 *               the grey in between, so no one level splits it from the field
 *               (Otsu's falls on the field's bright side, and the band takes
 *               the field). Its pixels DIFFER from the field instead: the
 *               band is the field's level (backgroundLevel(): the histogram's
 *               peak) +- a gap, and what lies OUTSIDE it passes
 *               (filters.bandMask `outside`); the rim then closes round the
 *               centre and the hole is filled; each particle's edge is then
 *               set half-way to its own darkest rim (filters.halfwayEdges).
 *               The gap is differsReach of the way to the darkest rims (at
 *               least the field's noise): it decides which particles are
 *               found, the half-way cut where their edges lie.
 *   brush       utils/masks/paint.js paints and erases the mask by hand.
 *   analyse     analyseMask(): 8-connected components of the mask, each one
 *               spore; a component touching the picture's border (cut by the
 *               frame) or under `minAreaPx` pixels is dropped. Per component:
 *               area (pixels), perimeter (the Moore contour,
 *               raster.traceContours), circularity 4piA/P^2 clamped to
 *               [0, 1], centroid, second central moments -> the equivalent
 *               ellipse: length = 4 sqrt(lambda_max), width = 4
 *               sqrt(lambda_min) and its orientation; the bounding box; and
 *               the outline, the contour simplified (Douglas-Peucker) to at
 *               most 200 points.
 *
 * The server measures the same mask itself when an analysis is saved
 * (routes/masks.py measure_spores, the same rule step for step; both are
 * tested against utils/masks/mask_cases.json), so what the page shows is what
 * is stored. Everything is reported in IMAGE pixels: every coordinate and
 * length is multiplied by `scale` = imageWidth / gridWidth, every area by
 * scale squared. metrics.js turns image pixels into micrometres.
 */
import { gaussianBlur, labelComponents, traceContours } from '../annotation/raster';
import { countMask } from '../masks/rle';
import { union } from '../masks/paint';

export const DEFAULTS = Object.freeze({
  //: the analysis grid's long side: a cellSens camera's 1920 px are read whole, not halved to 1024
  //: (the server takes a mask of up to 2048 x 2048, routes/masks.py MAX_PIXELS)
  gridMaxSide: 2048,
  sigma: 1,
  //: analysis pixels: a 3 x 4 px speck is grain, not a spore (routes/masks.py MIN_SPORE_PX)
  minAreaPx: 12,
  //: holes filled inside a component on "Apply to mask", as a share of the analysis image
  maxHoleFraction: 0.02,
  //: fractions of the image: cellSens' scale-bar box, bottom right. On every one of the lab's 332
  //: micrographs (0.7x to 100x) the box lies within the right 14% and the bottom 3.5%; the margin
  //: is the rest. filters.thresholdPreview leaves out each particle that reaches into it, whole.
  excludeRect: Object.freeze({ x: 0.84, y: 0.94, w: 0.16, h: 0.06 }),
  //: a picture whose mean grey level (0-255) is at least this is lit from behind: its spores are
  //: dark. The lab's darkest brightfield micrograph has a mean of 116; darkfield is far below.
  brightfieldMean: 96,
  //: "Auto (colourless spores)": the band reaches this share of the way from the field's level to
  //: the darkest rims (the 0.5th percentile), so an edge is cut near half-way, where it is...
  differsReach: 0.4,
  //: ...but never inside the field's own noise (this many sigma, at least differsMinGap levels)...
  differsK: 5,
  differsMinGap: 4,
  //: ...nor so wide that dark tissue on the slide pushes the faint rims out
  differsMaxGap: 32,
  maxOutlinePoints: 200,
});

export const POLARITIES = ['dark', 'light'];

const round2 = v => Math.round(v * 100) / 100;
const round4 = v => Math.round(v * 10000) / 10000;

/**
 * Otsu's threshold over `values` (any range), with 256 bins; with `mask`, over
 * the values whose mask entry is set.
 * Returns {threshold, below, above}: the value, and how many fall on each side.
 */
export function otsuThreshold(values, bins = 256, mask = null) {
  const counted = i => !mask || mask[i];
  let n = 0;
  let lo = Infinity;
  let hi = -Infinity;
  for (let i = 0; i < values.length; i += 1) {
    if (!counted(i)) continue;
    n += 1;
    const v = values[i];
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  if (!n) return { threshold: 0, below: 0, above: 0 };
  if (!(hi > lo)) return { threshold: lo, below: n, above: 0 };
  const width = (hi - lo) / bins;
  const hist = new Float64Array(bins);
  for (let i = 0; i < values.length; i += 1) {
    if (counted(i)) hist[Math.min(bins - 1, Math.floor((values[i] - lo) / width))] += 1;
  }
  let total = 0;
  for (let b = 0; b < bins; b += 1) total += hist[b] * (b + 0.5);
  let wB = 0;
  let sumB = 0;
  let best = -1;
  let at = 0;
  for (let b = 0; b < bins - 1; b += 1) {
    wB += hist[b];
    if (!wB) continue;
    const wF = n - wB;
    if (!wF) break;
    sumB += hist[b] * (b + 0.5);
    const mB = sumB / wB;
    const mF = (total - sumB) / wF;
    const between = wB * wF * (mB - mF) ** 2;
    if (between > best) {
      best = between;
      at = b;
    }
  }
  // Pixels in bins 0..at are "below": the threshold is that bin's upper edge.
  const threshold = lo + (at + 1) * width;
  let below = 0;
  for (let b = 0; b <= at; b += 1) below += hist[b];
  return { threshold, below, above: n - below };
}

/** The picture's grey level, 0-255 (Rec. 601 luma), blurred with `sigma` px. */
export function grayscale(rgba, w, h, sigma = DEFAULTS.sigma) {
  const gray = new Float32Array(w * h);
  for (let i = 0; i < gray.length; i += 1) {
    gray[i] = 0.299 * rgba[i * 4] + 0.587 * rgba[i * 4 + 1] + 0.114 * rgba[i * 4 + 2];
  }
  return sigma > 0 ? gaussianBlur(gray, w, h, sigma) : gray;
}

/**
 * Where the threshold band starts: Otsu's threshold on the grey levels, as a
 * whole number 0-255, with the spores on the side the picture's brightness
 * gives: dark on a bright picture (mean grey at or above brightfieldMean),
 * light on a dark one. {threshold, polarity}.
 */
export function autoThreshold(gray) {
  const { threshold } = otsuThreshold(gray, 256);
  let sum = 0;
  for (let i = 0; i < gray.length; i += 1) sum += gray[i];
  const polarity = gray.length && sum / gray.length < DEFAULTS.brightfieldMean ? 'light' : 'dark';
  // The split as a whole level on the spores' side of it.
  const level = polarity === 'dark' ? Math.floor(threshold) : Math.ceil(threshold);
  return { threshold: Math.max(0, Math.min(255, level)), polarity };
}

/**
 * The field's grey level and its noise: the histogram's highest bin (whole
 * levels), and sigma from the peak's half width at half its height (a normal
 * curve's is 1.1774 sigma), at least 0.85 -- one level of rounding. {level, sigma}.
 */
export function backgroundLevel(gray) {
  const hist = new Float64Array(256);
  for (let i = 0; i < gray.length; i += 1) hist[Math.max(0, Math.min(255, Math.round(gray[i])))] += 1;
  let level = 0;
  for (let v = 1; v < 256; v += 1) if (hist[v] > hist[level]) level = v;
  const half = hist[level] / 2;
  let lo = level;
  while (lo > 0 && hist[lo - 1] > half) lo -= 1;
  let hi = level;
  while (hi < 255 && hist[hi + 1] > half) hi += 1;
  // Each side's half width runs to the half-height crossing: the bin past the last one above it.
  const hwhm = ((level - lo + 1) + (hi - level + 1)) / 2;
  return { level, sigma: Math.max(0.85, hwhm / 1.1774) };
}

/**
 * "Auto (colourless spores)": the band round the field's level, to be kept
 * OUTSIDE. Its gap either side is differsReach of the way from the field to
 * the darkest rims (the 0.5th percentile), at least k sigma (and
 * differsMinGap), at most differsMaxGap. {min, max, outside: true, level, sigma, gap}.
 */
export function autoDiffers(gray, k = DEFAULTS.differsK) {
  const { level, sigma } = backgroundLevel(gray);
  const hist = new Float64Array(256);
  for (let i = 0; i < gray.length; i += 1) hist[Math.max(0, Math.min(255, Math.round(gray[i])))] += 1;
  let dark = 0;
  for (let seen = hist[0]; seen < gray.length * 0.005 && dark < 255; seen += hist[dark]) dark += 1;
  const floor = Math.max(DEFAULTS.differsMinGap, Math.round(k * sigma));
  const reach = Math.round(DEFAULTS.differsReach * Math.max(0, level - dark));
  const gap = Math.max(floor, Math.min(DEFAULTS.differsMaxGap, reach));
  return { min: Math.max(0, level - gap), max: Math.min(255, level + gap), outside: true, level, sigma, gap };
}

/** Fill background components that touch no border and hold at most maxPx pixels. */
export function fillSmallHoles(mask, w, h, maxPx) {
  const background = new Uint8Array(w * h);
  for (let i = 0; i < mask.length; i += 1) background[i] = mask[i] ? 0 : 1;
  const { labels, sizes, touchesBorder } = labelComponents(background, w, h, 4);
  const out = mask.slice();
  for (let i = 0; i < labels.length; i += 1) {
    const l = labels[i];
    if (l && !touchesBorder[l] && sizes[l] <= maxPx) out[i] = 1;
  }
  return out;
}

/** "Apply to mask": the preview, its enclosed holes filled, added to the mask (a copy). */
export function applyThreshold(mask, preview, w, h, maxHoleFraction = DEFAULTS.maxHoleFraction) {
  return union(mask, fillSmallHoles(preview, w, h, Math.max(1, Math.round(maxHoleFraction * w * h))));
}

/** The length of a closed polygon through `points`. */
export function perimeterOf(points) {
  const n = points.length;
  if (n < 2) return 0;
  let total = 0;
  for (let i = 0; i < n; i += 1) {
    const a = points[i];
    const b = points[(i + 1) % n];
    total += Math.hypot(b[0] - a[0], b[1] - a[1]);
  }
  return total;
}

function perpendicular(p, a, b) {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len2 = dx * dx + dy * dy;
  if (!len2) return Math.hypot(p[0] - a[0], p[1] - a[1]);
  const t = Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len2));
  return Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy));
}

/** Douglas-Peucker on a closed outline, in the outline's own units. */
export function simplifyOutline(points, tolerance) {
  if (points.length <= 3 || tolerance <= 0) return points.slice();
  const keep = new Uint8Array(points.length);
  keep[0] = 1;
  keep[points.length - 1] = 1;
  const stack = [[0, points.length - 1]];
  while (stack.length) {
    const [first, last] = stack.pop();
    let index = -1;
    let max = 0;
    for (let i = first + 1; i < last; i += 1) {
      const d = perpendicular(points[i], points[first], points[last]);
      if (d > max) {
        max = d;
        index = i;
      }
    }
    if (index !== -1 && max > tolerance) {
      keep[index] = 1;
      stack.push([first, index], [index, last]);
    }
  }
  const out = [];
  for (let i = 0; i < points.length; i += 1) if (keep[i]) out.push(points[i]);
  return out;
}

/** The outline with at most `max` points: the tolerance doubles until it fits. */
export function limitOutline(points, max) {
  // A very long outline (a big clump at full resolution: a million points) is thinned evenly
  // first; Douglas-Peucker over all of it, up to 16 times, took seconds. Outlines are drawn, never measured.
  const thin = points.length > max * 16 ? points.filter((_, i) => i % Math.ceil(points.length / (max * 16)) === 0) : points;
  let out = thin;
  let tolerance = 0.35;
  for (let attempt = 0; out.length > max && attempt < 16; attempt += 1) {
    out = simplifyOutline(thin, tolerance);
    tolerance *= 1.6;
  }
  return out.length > max ? out.filter((_, i) => i % Math.ceil(out.length / max) === 0) : out;
}

/**
 * Area, centroid, central moments and bounding box of every label, in one
 * pass over the image. Index 0 is unused (the background).
 */
export function componentMoments(labels, w, h, count) {
  const n = count + 1;
  const area = new Float64Array(n);
  const sx = new Float64Array(n);
  const sy = new Float64Array(n);
  const sxx = new Float64Array(n);
  const syy = new Float64Array(n);
  const sxy = new Float64Array(n);
  const minX = new Float64Array(n).fill(Infinity);
  const minY = new Float64Array(n).fill(Infinity);
  const maxX = new Float64Array(n).fill(-Infinity);
  const maxY = new Float64Array(n).fill(-Infinity);
  for (let y = 0; y < h; y += 1) {
    const cy = y + 0.5;
    for (let x = 0; x < w; x += 1) {
      const l = labels[y * w + x];
      if (!l) continue;
      const cx = x + 0.5;
      area[l] += 1;
      sx[l] += cx;
      sy[l] += cy;
      sxx[l] += cx * cx;
      syy[l] += cy * cy;
      sxy[l] += cx * cy;
      if (x < minX[l]) minX[l] = x;
      if (x + 1 > maxX[l]) maxX[l] = x + 1;
      if (y < minY[l]) minY[l] = y;
      if (y + 1 > maxY[l]) maxY[l] = y + 1;
    }
  }
  const out = [];
  for (let l = 1; l <= count; l += 1) {
    const a = area[l];
    const mx = sx[l] / a;
    const my = sy[l] / a;
    // The pixel's own spread (1/12) keeps a one-pixel-wide line from a zero width.
    const uxx = sxx[l] / a - mx * mx + 1 / 12;
    const uyy = syy[l] / a - my * my + 1 / 12;
    const uxy = sxy[l] / a - mx * my;
    const common = (uxx + uyy) / 2;
    const diff = Math.sqrt(((uxx - uyy) / 2) ** 2 + uxy * uxy);
    const lambdaMax = Math.max(0, common + diff);
    const lambdaMin = Math.max(0, common - diff);
    out.push({
      label: l, area: a, centroid: [mx, my],
      length: 4 * Math.sqrt(lambdaMax), width: 4 * Math.sqrt(lambdaMin),
      orientation: (0.5 * Math.atan2(2 * uxy, uxx - uyy) * 180) / Math.PI,
      bbox: [minX[l], minY[l], maxX[l] - minX[l], maxY[l] - minY[l]],
    });
  }
  return out;
}

/**
 * The spores of a mask (w x h, the analysis grid): one per 8-connected
 * component, numbered in raster order of its first pixel, a component that
 * touches the border or holds under `minAreaPx` pixels dropped (counted in
 * `dropped`). opts.imageWidth / imageHeight: the picture's real size, to
 * report image pixels (default: the grid's, no scaling).
 */
export function analyseMask(mask, w, h, opts = {}) {
  const o = { ...DEFAULTS, ...opts };
  const imageWidth = o.imageWidth || w;
  const scale = imageWidth / w;
  const first = labelComponents(mask, w, h, 8);
  const dropped = { border: 0, small: 0 };
  const keep = new Uint8Array(first.count + 1);
  for (let l = 1; l <= first.count; l += 1) {
    if (first.touchesBorder[l]) dropped.border += 1;
    else if (first.sizes[l] < o.minAreaPx) dropped.small += 1;
    else keep[l] = 1;
  }
  const kept = new Uint8Array(w * h);
  for (let i = 0; i < kept.length; i += 1) kept[i] = keep[first.labels[i]] ? 1 : 0;
  // Labelled again on the kept mask: traceContours labels the same way (8-connected,
  // raster order), so contour k belongs to label k + 1.
  const final = labelComponents(kept, w, h, 8);
  const moments = componentMoments(final.labels, w, h, final.count);
  const contours = traceContours(kept, w, h, 1);
  const spores = moments.map((m, k) => {
    const contour = contours[k] || [m.centroid];
    const perimeter = perimeterOf(contour);
    const circularity = perimeter > 0 ? Math.min(1, (4 * Math.PI * m.area) / (perimeter * perimeter)) : 1;
    const outline = limitOutline(contour, o.maxOutlinePoints).map(([x, y]) => [round2(x * scale), round2(y * scale)]);
    return {
      idx: k + 1,
      area_px: round2(m.area * scale * scale),
      perimeter_px: round2(perimeter * scale),
      circularity: round4(circularity),
      length_px: round2(m.length * scale),
      width_px: round2(m.width * scale),
      orientation_deg: round2(m.orientation),
      centroid_px: [round2(m.centroid[0] * scale), round2(m.centroid[1] * scale)],
      bbox_px: m.bbox.map(v => round2(v * scale)),
      outline_px: outline,
    };
  });
  return {
    spores, scale: round4(scale), dropped, pixels: countMask(mask),
    analysis: { width: w, height: h }, image: { width: imageWidth, height: o.imageHeight || Math.round(h * scale) },
  };
}
